import React, { useCallback, useState, useMemo } from 'react';
import { ActiveContractItemProps } from './interfaces/ActiveContractItemProps';
import { useTranslation } from 'react-i18next';
import ActiveContractScreensItem from '../ActiveContractScreensItem/ActiveContractScreensItem';
import ActiveCyclesWIthoutScreen from '../ActiveCyclesWIthoutScreen/ActiveCyclesWIthoutScreen';

const ActiveContractItem: React.FC<ActiveContractItemProps> = ({
  contractName,
  contractId,
  contractIsCelsius,
  screens,
  onDeleteScreen,
  isLoading,
  cyclesWithoutScreens,
  onDecisionStateChange,
}) => {
  const filteredScreensByContract = screens.filter((screen) => screen.contractId === contractId);

  // track per-screen reported state: true = has cycles, false = reported no cycles, undefined = not reported yet
  const [screensCycleState, setScreensCycleState] = useState<Record<string, boolean | undefined>>({});

  const onCyclesStateChange = useCallback((screenId: string, hasCycles: boolean) => {
    setScreensCycleState((prev) => {
      if (prev[screenId] === hasCycles) return prev;
      return { ...prev, [screenId]: hasCycles };
    });
  }, []);

  const shouldRenderContract = useMemo(() => {
    const hasCyclesWithoutScreens = Array.isArray(cyclesWithoutScreens) && cyclesWithoutScreens.length > 0;
    if (!filteredScreensByContract.length) {
      return hasCyclesWithoutScreens;
    }
    const screenIds = filteredScreensByContract.map((s) => s.id);
    const allReported = screenIds.every((id) => screensCycleState[id] !== undefined);
    const anyHasCycles = screenIds.some((id) => screensCycleState[id] === true);
    // while some screens haven't reported yet, keep showing the contract (avoid flicker)
    if (!allReported) return true;
    // all reported and none have cycles and no cycles without screens -> hide
    if (!anyHasCycles && !hasCyclesWithoutScreens) return false;
    return true;
  }, [filteredScreensByContract, screensCycleState, cyclesWithoutScreens]);

  // decision pending: any child screen hasn't reported yet, or when no screens exist and cyclesWithoutScreens data still loading
  const isDecisionPending = useMemo(() => {
    if (filteredScreensByContract.length > 0) {
      const screenIds = filteredScreensByContract.map((s) => s.id);
      return screenIds.some((id) => screensCycleState[id] === undefined);
    }
    // when there are no screens for this contract, wait while cyclesWithoutScreens is loading
    return Boolean(isLoading);
  }, [filteredScreensByContract, screensCycleState, isLoading]);

  // inform parent about decision pending state
  React.useEffect(() => {
    if (typeof onDecisionStateChange === 'function') {
      onDecisionStateChange(contractId, isDecisionPending);
    }
  }, [contractId, isDecisionPending, onDecisionStateChange]);

  const { t } = useTranslation();
  const contractTitle = `${t('labels.contractName')}: ${contractName}`;

  return (
    <>
      {!shouldRenderContract ? null : (
        <li className="c-active-contract-item relative flex flex-col gap-y-1 overflow-hidden rounded-lg border border-foggy-mist bg-white p-2 shadow-15 dark:border-strokedark dark:bg-boxdark">
          <div className="flex-auto">
            <span className="c-active-contract-item__screen-name inline-block pb-1 text-base font-bold md:text-xl">
              {contractTitle}
            </span>
            <ul className="flex flex-col gap-y-1">
              {filteredScreensByContract.map((screen) => (
                <li key={screen.id}>
                  <ActiveContractScreensItem
                    name={screen.name}
                    screenId={screen.id}
                    screenContractId={contractId}
                    contractIsCelsius={contractIsCelsius}
                    onDeleteScreen={onDeleteScreen}
                    criticalDeviation={screen.critical_deviation}
                    onCyclesStateChange={onCyclesStateChange}
                  />
                </li>
              ))}
            </ul>
          </div>
          <ActiveCyclesWIthoutScreen isLoading={isLoading} cycles={cyclesWithoutScreens} contractId={contractId} />
        </li>
      )}
    </>
  );
};

export default ActiveContractItem;
